import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { GraduationCap, Camera, CheckCircle2, ChevronDown, ChevronUp, Coins, Plus, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const commonSubjects = [
  'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'Hindi', 'Social Science',
  'Computer Science', 'Accountancy', 'Business Studies', 'Economics', 'History', 'Geography',
  'Political Science', 'Psychology', 'Sociology', 'Physical Education', 'Sanskrit', 'French',
  'German', 'Spanish', 'Engineering', 'Medical', 'Law', 'Commerce', 'Arts', 'Science',
  'Nursery Education', 'Kindergarten', 'Pre-School', 'Play School', 'Montessori',
  'Music - Vocal', 'Music - Instrumental', 'Guitar', 'Piano', 'Keyboard', 'Drums', 'Tabla',
  'Harmonium', 'Violin', 'Flute', 'Classical Music', 'Western Music', 'Hindustani Music',
  'Carnatic Music', 'Painting', 'Drawing', 'Sketching', 'Calligraphy', 'Arts & Crafts',
  'Dance - Classical', 'Dance - Contemporary', 'Bharatanatyam', 'Kathak', 'Odissi',
  'Hip Hop', 'Salsa', 'Ballet', 'Yoga', 'Meditation', 'Fitness', 'Gym Training',
  'Aerobics', 'Zumba', 'Martial Arts', 'Karate', 'Taekwondo',
  'English Speaking', 'IELTS', 'TOEFL', 'Spoken English', 'Business English',
  'German Language', 'French Language', 'Spanish Language', 'Japanese', 'Korean',
  'Mandarin', 'Arabic', 'Punjabi', 'Urdu',
  'Coding', 'Programming', 'Web Development', 'App Development', 'Data Science',
  'Machine Learning', 'Artificial Intelligence', 'Digital Marketing', 'Photography',
  'Videography', 'Graphic Design', 'UI/UX Design', 'Content Writing', 'Public Speaking',
  'Chess', 'Cooking', 'Baking', 'Stitching', 'Embroidery', 'Interior Design',
  'Fashion Design', 'Jewellery Making', 'Pottery', 'Gardening',
  'IIT-JEE', 'NEET', 'CAT', 'GATE', 'UPSC', 'SSC', 'Banking Exams', 'Railway Exams',
  'Other'
];

const commonLanguages = [
  'English', 'Hindi', 'Punjabi', 'Urdu', 'Sanskrit', 'French', 'German', 'Spanish',
  'Mandarin', 'Japanese', 'Korean', 'Arabic', 'Bengali', 'Tamil', 'Telugu', 'Malayalam',
  'Kannada', 'Marathi', 'Gujarati', 'Odia', 'Assamese'
];

const tricityLocations = [
  'Chandigarh - Sector 1', 'Chandigarh - Sector 2', 'Chandigarh - Sector 3', 'Chandigarh - Sector 4', 'Chandigarh - Sector 5',
  'Chandigarh - Sector 6', 'Chandigarh - Sector 7', 'Chandigarh - Sector 8', 'Chandigarh - Sector 9', 'Chandigarh - Sector 10',
  'Chandigarh - Sector 11', 'Chandigarh - Sector 12', 'Chandigarh - Sector 14', 'Chandigarh - Sector 15', 'Chandigarh - Sector 16',
  'Chandigarh - Sector 17', 'Chandigarh - Sector 18', 'Chandigarh - Sector 19', 'Chandigarh - Sector 20', 'Chandigarh - Sector 21',
  'Chandigarh - Sector 22', 'Chandigarh - Sector 23', 'Chandigarh - Sector 24', 'Chandigarh - Sector 25', 'Chandigarh - Sector 26',
  'Chandigarh - Sector 27', 'Chandigarh - Sector 28', 'Chandigarh - Sector 29', 'Chandigarh - Sector 30', 'Chandigarh - Sector 31',
  'Chandigarh - Sector 32', 'Chandigarh - Sector 33', 'Chandigarh - Sector 34', 'Chandigarh - Sector 35', 'Chandigarh - Sector 36',
  'Chandigarh - Sector 37', 'Chandigarh - Sector 38', 'Chandigarh - Sector 39', 'Chandigarh - Sector 40', 'Chandigarh - Sector 41',
  'Chandigarh - Sector 42', 'Chandigarh - Sector 43', 'Chandigarh - Sector 44', 'Chandigarh - Sector 45', 'Chandigarh - Sector 46',
  'Chandigarh - Sector 47', 'Chandigarh - Sector 48', 'Chandigarh - Sector 49', 'Chandigarh - Sector 50', 'Chandigarh - Sector 51',
  'Chandigarh - Sector 52', 'Chandigarh - Sector 53', 'Chandigarh - Sector 54', 'Chandigarh - Sector 55', 'Chandigarh - Sector 56',
  'Chandigarh - Manimajra', 'Chandigarh - Dhanas', 'Chandigarh - Maloya', 'Chandigarh - Daria', 'Chandigarh - Hallomajra',
  'Chandigarh - Kaimbwala', 'Chandigarh - Khuda Ali Sher', 'Chandigarh - Burail', 'Chandigarh - Attawa',
  'Chandigarh - IT Park', 'Chandigarh - PGI', 'Chandigarh - Panjab University Campus', 'Chandigarh - Industrial Area Phase 1',
  'Chandigarh - Industrial Area Phase 2', 'Chandigarh - Grain Market',
  'Mohali - Phase 1', 'Mohali - Phase 2', 'Mohali - Phase 3A', 'Mohali - Phase 3B', 'Mohali - Phase 4', 'Mohali - Phase 4A',
  'Mohali - Phase 5', 'Mohali - Phase 6', 'Mohali - Phase 7', 'Mohali - Phase 8', 'Mohali - Phase 8A', 'Mohali - Phase 8B',
  'Mohali - Phase 9', 'Mohali - Phase 10', 'Mohali - Phase 11', 'Mohali - Sector 58', 'Mohali - Sector 59',
  'Mohali - Sector 60', 'Mohali - Sector 61', 'Mohali - Sector 62', 'Mohali - Sector 63', 'Mohali - Sector 64',
  'Mohali - Sector 65', 'Mohali - Sector 66', 'Mohali - Sector 67', 'Mohali - Sector 68', 'Mohali - Sector 69',
  'Mohali - Sector 70', 'Mohali - Sector 71', 'Mohali - Sector 72', 'Mohali - Sector 74', 'Mohali - Sector 76',
  'Mohali - Sector 77', 'Mohali - Sector 78', 'Mohali - Sector 79', 'Mohali - Sector 80', 'Mohali - Sector 82',
  'Mohali - Sector 86', 'Mohali - Sector 88', 'Mohali - Sector 91', 'Mohali - Aerocity', 'Mohali - IT City',
  'Mohali - VR Punjab', 'Mohali - Kharar Road', 'Mohali - Airport Road', 'Mohali - Balongi', 'Mohali - Sohana',
  'Panchkula - Sector 1', 'Panchkula - Sector 2', 'Panchkula - Sector 3', 'Panchkula - Sector 4', 'Panchkula - Sector 5',
  'Panchkula - Sector 6', 'Panchkula - Sector 7', 'Panchkula - Sector 8', 'Panchkula - Sector 9', 'Panchkula - Sector 10',
  'Panchkula - Sector 11', 'Panchkula - Sector 12', 'Panchkula - Sector 12A', 'Panchkula - Sector 14', 'Panchkula - Sector 15',
  'Panchkula - Sector 16', 'Panchkula - Sector 17', 'Panchkula - Sector 18', 'Panchkula - Sector 19', 'Panchkula - Sector 20',
  'Panchkula - Sector 21', 'Panchkula - Sector 25', 'Panchkula - Sector 26', 'Panchkula - Sector 27', 'Panchkula - Sector 28',
  'Panchkula - MDC', 'Panchkula - Pinjore', 'Panchkula - Kalka', 'Panchkula - Barwala', 'Panchkula - Ramgarh',
  'Zirakpur - VIP Road', 'Zirakpur - Baltana', 'Zirakpur - Pabhat', 'Zirakpur - Dhakoli', 'Zirakpur - Peer Muchalla',
  'Zirakpur - Gazipur', 'Zirakpur - Lohgarh', 'Zirakpur - NAC', 'Zirakpur - Maya Garden City', 'Zirakpur - PR7 Airport Road',
  'Kharar', 'Kharar - Landran', 'Kharar - Banur', 'Derabassi', 'Derabassi - Industrial Area', 'Mullanpur',
  'Mullanpur - New Chandigarh', 'New Chandigarh - Omaxe', 'New Chandigarh - DLF', 'Nayagaon', 'Naya Gaon - Sector 1',
  'Naya Gaon - Sector 2', 'Kurali', 'Morinda', 'Gharuan', 'Lalru', 'Rajpura',
  'Village Behlana', 'Village Manauli', 'Village Sarangpur', 'Village Khuda Lahora', 'Village Khuda Jassu',
  'Village Dadumajra', 'Village Kishangarh', 'Village Majri', 'Village Raipur Khurd', 'Village Raipur Kalan'
];

const classCategories = {
  'School': ['Playgroup', 'Nursery', 'LKG', 'UKG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'],
  'College': ['UG Year 1', 'UG Year 2', 'UG Year 3', 'UG Year 4', 'PG Year 1', 'PG Year 2', 'PhD'],
  'Skills': ['Beginner', 'Intermediate', 'Advanced', 'Professional', 'All Ages', 'Kids (3-10)', 'Teens (11-17)', 'Adults (18+)']
};

const emptyProfile = {
  name: '',
  mobile: '',
  gender: '',
  location: '',
  education: {
    tenth: { school: '', board: '', year: '' },
    twelfth: { school: '', board: '', year: '' },
    graduation: { degree: '', college: '', year: '' },
    postgraduation: { degree: '', college: '', year: '' },
    phd: { specialization: '', university: '', year: '' },
    diploma: { course: '', institute: '', year: '' },
    other_courses: []
  },
  experience: [],
  subjects: [],
  languages: [],
  fee_min: 0,
  fee_max: 0,
  can_travel: false,
  teaches_online: false,
  online_experience: '',
  teaches_at_home: false,
  homework_help: false,
  works_as: '',
  total_teaching_exp: '',
  intro_video_url: '',
  profile_photo: ''
};

export default function TutorProfileEdit({ user }) {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [savingEducation, setSavingEducation] = useState(false);
  const [savingExperience, setSavingExperience] = useState(false);
  const [pendingScrollTarget, setPendingScrollTarget] = useState('');
  const [savedEducationSnapshot, setSavedEducationSnapshot] = useState(JSON.stringify(emptyProfile.education));
  const [savedExperienceSnapshot, setSavedExperienceSnapshot] = useState(JSON.stringify(emptyProfile.experience));
  const [profileData, setProfileData] = useState(emptyProfile);
  const [newExperience, setNewExperience] = useState({ role: '', company_institute: '', duration: '' });
  const [newSubject, setNewSubject] = useState({ subject: '', classes: [] });
  const [newCourse, setNewCourse] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [subjectSearch, setSubjectSearch] = useState('');
  const [languageSearch, setLanguageSearch] = useState('');
  const [quickStartOnly, setQuickStartOnly] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (!pendingScrollTarget) return;
    const timer = window.setTimeout(() => {
      scrollToSection(pendingScrollTarget);
      setPendingScrollTarget('');
    }, 120);

    return () => window.clearTimeout(timer);
  }, [pendingScrollTarget, quickStartOnly]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    const headerOffset = 104;
    const elementTop = element.getBoundingClientRect().top + window.scrollY;
    const targetTop = Math.max(0, elementTop - headerOffset);
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  };

  const loadProfile = async () => {
    try {
      const response = await api.get('/tutor/profile');
      const nextProfile = { ...emptyProfile, ...response.data, education: { ...emptyProfile.education, ...(response.data.education || {}) } };
      setProfileData(nextProfile);
      setSavedEducationSnapshot(JSON.stringify(nextProfile.education || emptyProfile.education));
      setSavedExperienceSnapshot(JSON.stringify(nextProfile.experience || emptyProfile.experience));
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const handleSaveEducation = async () => {
    setSavingEducation(true);
    try {
      await api.put('/tutor/profile', { education: profileData.education || emptyProfile.education });
      setSavedEducationSnapshot(JSON.stringify(profileData.education || emptyProfile.education));
      toast.success('Education saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save education');
    } finally {
      setSavingEducation(false);
    }
  };

  const handleSaveExperience = async () => {
    setSavingExperience(true);
    try {
      await api.put('/tutor/profile', { experience: profileData.experience || [] });
      setSavedExperienceSnapshot(JSON.stringify(profileData.experience || []));
      toast.success('Experience saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save experience');
    } finally {
      setSavingExperience(false);
    }
  };

  const handleSaveProfile = async (goToJobs = false) => {
    if (!profileData.name || !profileData.mobile || !profileData.location) {
      toast.error('Please fill Name, Mobile, and Location');
      return;
    }

    if (String(profileData.mobile).length !== 10) {
      toast.error('Mobile number must be exactly 10 digits');
      return;
    }

    if (!profileData.subjects || profileData.subjects.length === 0) {
      toast.error('Please add at least one subject you can teach');
      return;
    }

    if (!profileData.fee_min || !profileData.fee_max || profileData.fee_min === 0 || profileData.fee_max === 0) {
      toast.error('Please set your fee range');
      return;
    }

    if (profileData.fee_min >= profileData.fee_max) {
      toast.error('Maximum fee must be greater than minimum fee');
      return;
    }

    if (!(profileData.can_travel || profileData.teaches_online || profileData.teaches_at_home)) {
      toast.error('Please select at least one teaching mode');
      return;
    }

    setLoading(true);
    try {
      const { profile_photo, ...profilePayload } = profileData;
      await api.put('/tutor/profile', profilePayload);
      toast.success(String(profileData.profile_photo || '').trim()
        ? 'Profile updated. Your quick profile is ready.'
        : 'Profile saved. Add a photo to appear in tutor listings.');
      navigate(goToJobs ? '/tutor/dashboard?tab=jobs' : '/tutor/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024) {
      toast.error('File too large. Maximum size is 100KB.');
      event.target.value = '';
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP files are allowed.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      setProfileData((prev) => ({ ...prev, profile_photo: loadEvent.target?.result || prev.profile_photo }));
    };
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('file', file);

    try {
      setPhotoUploading(true);
      toast.loading('Uploading photo...', { id: 'profile-photo-upload' });
      const response = await api.post('/tutor/profile/photo/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfileData((prev) => ({ ...prev, profile_photo: response.data.photo_url || prev.profile_photo }));
      toast.success('Profile photo uploaded', { id: 'profile-photo-upload' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo', { id: 'profile-photo-upload' });
    } finally {
      setPhotoUploading(false);
      event.target.value = '';
    }
  };

  const addExperience = () => {
    if (newExperience.role && newExperience.company_institute && newExperience.duration) {
      setProfileData((prev) => ({
        ...prev,
        experience: [...(prev.experience || []), newExperience]
      }));
      setNewExperience({ role: '', company_institute: '', duration: '' });
    }
  };

  const removeExperience = (index) => {
    const updated = [...(profileData.experience || [])];
    updated.splice(index, 1);
    setProfileData({ ...profileData, experience: updated });
  };

  const toggleClass = (className) => {
    const classes = newSubject.classes || [];
    if (classes.includes(className)) {
      setNewSubject({ ...newSubject, classes: classes.filter((item) => item !== className) });
      return;
    }
    setNewSubject({ ...newSubject, classes: [...classes, className] });
  };

  const addSubject = () => {
    const trimmedSubject = String(newSubject.subject || '').trim();
    if (!trimmedSubject || !newSubject.classes.length) return;

    setProfileData((prev) => ({
      ...prev,
      subjects: [...(prev.subjects || []), { subject: trimmedSubject, classes: [...newSubject.classes] }]
    }));
    setNewSubject({ subject: '', classes: [] });
    setSubjectSearch('');
  };

  const removeSubject = (index) => {
    const updated = [...(profileData.subjects || [])];
    updated.splice(index, 1);
    setProfileData({ ...profileData, subjects: updated });
  };

  const addCourse = () => {
    const trimmed = String(newCourse || '').trim();
    if (!trimmed) return;
    const courses = profileData.education?.other_courses || [];
    setProfileData({
      ...profileData,
      education: {
        ...profileData.education,
        other_courses: [...courses, trimmed]
      }
    });
    setNewCourse('');
  };

  const removeCourse = (index) => {
    const courses = [...(profileData.education?.other_courses || [])];
    courses.splice(index, 1);
    setProfileData({
      ...profileData,
      education: { ...profileData.education, other_courses: courses }
    });
  };

  const addLanguage = () => {
    const trimmed = String(newLanguage || '').trim();
    if (!trimmed) return;
    if ((profileData.languages || []).includes(trimmed)) return;
    setProfileData({
      ...profileData,
      languages: [...(profileData.languages || []), trimmed]
    });
    setNewLanguage('');
    setLanguageSearch('');
  };

  const removeLanguage = (index) => {
    const updated = [...(profileData.languages || [])];
    updated.splice(index, 1);
    setProfileData({ ...profileData, languages: updated });
  };

  const filteredSubjects = commonSubjects.filter((subject) => subject.toLowerCase().includes(subjectSearch.toLowerCase())).slice(0, 12);
  const filteredLanguages = commonLanguages.filter((language) => language.toLowerCase().includes(languageSearch.toLowerCase())).slice(0, 10);
  const educationDirty = JSON.stringify(profileData.education || emptyProfile.education) !== savedEducationSnapshot;
  const experienceDraftDirty = !!(newExperience.role || newExperience.company_institute || newExperience.duration);
  const experienceDirty = JSON.stringify(profileData.experience || []) !== savedExperienceSnapshot || experienceDraftDirty;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <header className="backdrop-blur-xl bg-white/70 border-b border-white/20 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-outfit font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
              Tricity Tutors
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <Link to="/wallet">
              <Button variant="outline" className="rounded-full border-slate-300 bg-white">
                <Coins className="mr-2 h-4 w-4 text-amber-500" />
                {user?.coins || 0}
              </Button>
            </Link>
            <Button variant="outline" className="rounded-full border-slate-300 bg-white" onClick={() => navigate('/tutor/dashboard')}>
              Back to Dashboard
            </Button>
            <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={() => handleSaveProfile(false)} disabled={loading || photoUploading}>
              <Save className="mr-2 h-4 w-4" />
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
          <section className="space-y-6">
          <div id="full-details-section" className="scroll-mt-28" />
          <Card className="border-0 bg-white shadow-xl shadow-slate-200/70">
            <CardHeader className="pb-3">
              <CardTitle className="font-outfit text-2xl text-slate-900">1. Essential Profile</CardTitle>
              <p className="text-sm text-slate-500">
                Keep this fast. These are the fields needed to make the profile usable.
              </p>
            </CardHeader>
            <CardContent className="grid gap-6">
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    data-testid="profile-name-input"
                    value={profileData.name || ''}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    className="mt-2 h-12 rounded-2xl border-slate-200 bg-slate-50"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <Label>Mobile Number *</Label>
                  <Input
                    data-testid="profile-mobile-input"
                    type="tel"
                    value={profileData.mobile || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 10) {
                        setProfileData({ ...profileData, mobile: value });
                      }
                    }}
                    maxLength={10}
                    className="mt-2 h-12 rounded-2xl border-slate-200 bg-slate-50"
                    placeholder="10-digit mobile number"
                  />
                </div>
                <div>
                  <Label>Location *</Label>
                  <Input
                    data-testid="profile-location-select"
                    list="tricity-locations"
                    value={profileData.location || ''}
                    onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                    className="mt-2 h-12 rounded-2xl border-slate-200 bg-slate-50"
                    placeholder="Start typing your area"
                  />
                  <datalist id="tricity-locations">
                    {tricityLocations.map((loc) => <option key={loc} value={loc} />)}
                  </datalist>
                </div>
              </div>

              <div className="grid gap-5">
                <div className="rounded-[28px] border border-slate-200 bg-slate-50/90 p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">What do you teach?</h3>
                      <p className="text-sm text-slate-500">Add one subject first. You can always add more later.</p>
                    </div>
                    <Badge className="rounded-full bg-indigo-100 px-3 py-1 text-indigo-700">
                      {profileData.subjects?.length || 0} added
                    </Badge>
                  </div>

                  {(profileData.subjects || []).length > 0 && (
                    <div className="mb-4 space-y-3">
                      {profileData.subjects.map((subj, idx) => (
                        <div key={`${subj.subject}-${idx}`} className="rounded-2xl border border-indigo-100 bg-white p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="font-semibold text-slate-900">{subj.subject}</div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {subj.classes?.map((cls) => (
                                  <Badge key={cls} variant="secondary" className="rounded-full bg-slate-100 text-slate-700">
                                    {cls}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" className="rounded-full text-slate-500 hover:text-red-600" onClick={() => removeSubject(idx)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid gap-4">
                    <div>
                      <Label>Subject *</Label>
                      <Input
                        data-testid="subject-search-input"
                        list="common-subjects"
                        value={subjectSearch}
                        onChange={(e) => {
                          setSubjectSearch(e.target.value);
                          setNewSubject({ ...newSubject, subject: e.target.value });
                        }}
                        className="mt-2 h-12 rounded-2xl border-slate-200 bg-white"
                        placeholder="Maths, IELTS, Guitar, Coding..."
                      />
                      <datalist id="common-subjects">
                        {commonSubjects.map((subject) => <option key={subject} value={subject} />)}
                      </datalist>
                      {!!subjectSearch && filteredSubjects.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {filteredSubjects.map((subject) => (
                            <button
                              key={subject}
                              type="button"
                              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition hover:border-indigo-300 hover:text-indigo-700"
                              onClick={() => {
                                setSubjectSearch(subject);
                                setNewSubject({ ...newSubject, subject });
                              }}
                            >
                              {subject}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label>Levels you teach *</Label>
                      <div className="mt-3 space-y-4">
                        {Object.entries(classCategories).map(([category, classes]) => (
                          <div key={category}>
                            <div className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{category}</div>
                            <div className="flex flex-wrap gap-2">
                              {classes.map((cls) => {
                                const active = newSubject.classes?.includes(cls);
                                return (
                                  <button
                                    key={cls}
                                    type="button"
                                    data-testid={`class-option-${cls}-btn`}
                                    onClick={() => toggleClass(cls)}
                                    className={`rounded-full border px-3 py-1.5 text-sm transition ${
                                      active
                                        ? 'border-indigo-600 bg-indigo-600 text-white shadow'
                                        : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50'
                                    }`}
                                  >
                                    {cls}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button
                      data-testid="add-subject-btn"
                      type="button"
                      className="w-full rounded-2xl bg-indigo-600 py-6 text-base hover:bg-indigo-700 md:w-auto"
                      onClick={addSubject}
                      disabled={!String(newSubject.subject || '').trim() || !newSubject.classes?.length}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Subject
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <Button
              variant="outline"
              className="rounded-full border-slate-300 bg-white"
              onClick={() => handleSaveProfile(true)}
              disabled={loading || photoUploading}
            >
              Save and See Jobs
            </Button>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8" onClick={() => handleSaveProfile(false)} disabled={loading || photoUploading}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {loading ? 'Saving...' : 'Save Quick Profile'}
            </Button>
          </div>

          <Card className="border-0 bg-white/85 shadow-xl shadow-slate-200/70">
            <CardContent className="p-5">
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-[24px] bg-slate-50 px-5 py-4 text-left transition hover:bg-slate-100"
                onClick={() => {
                  const next = !quickStartOnly;
                  setQuickStartOnly(next);
                  if (!next) {
                    setPendingScrollTarget('full-details-section');
                  }
                }}
              >
                <div>
                  <div className="font-outfit text-xl font-bold text-slate-900">2. Add More Details Later</div>
                  <div className="text-sm text-slate-500">
                    Qualifications, experience, languages, intro video, and profile polish.
                  </div>
                </div>
                {quickStartOnly ? <ChevronDown className="h-5 w-5 text-slate-500" /> : <ChevronUp className="h-5 w-5 text-slate-500" />}
              </button>

              {!quickStartOnly && (
                <div className="mt-6 space-y-6">
                  <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                    <div>
                      <Label>Gender</Label>
                      <Select value={profileData.gender || ''} onValueChange={(value) => setProfileData({ ...profileData, gender: value })}>
                        <SelectTrigger data-testid="profile-gender-select" className="mt-2 h-12 rounded-2xl border-slate-200 bg-slate-50">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Works As</Label>
                      <Input
                        data-testid="profile-works-as-input"
                        value={profileData.works_as || ''}
                        onChange={(e) => setProfileData({ ...profileData, works_as: e.target.value })}
                        className="mt-2 h-12 rounded-2xl border-slate-200 bg-slate-50"
                        placeholder="Teacher, freelancer, trainer..."
                      />
                    </div>
                    <div>
                      <Label>Total Teaching Experience</Label>
                      <Input
                        data-testid="profile-total-exp-input"
                        value={profileData.total_teaching_exp || ''}
                        onChange={(e) => setProfileData({ ...profileData, total_teaching_exp: e.target.value })}
                        className="mt-2 h-12 rounded-2xl border-slate-200 bg-slate-50"
                        placeholder="e.g. 5 years"
                      />
                    </div>
                  </div>

                  <div className="space-y-6">
                    <Card className="border border-slate-200 bg-slate-50/80 shadow-none">
                      <CardHeader>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <CardTitle className="text-lg">Education</CardTitle>
                            <p className={`mt-1 text-xs font-medium ${educationDirty ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {educationDirty ? 'Unsaved changes' : 'Saved'}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant={educationDirty ? 'default' : 'outline'}
                            className="rounded-full"
                            onClick={handleSaveEducation}
                            disabled={savingEducation || !educationDirty}
                          >
                            {savingEducation ? 'Saving...' : educationDirty ? 'Save Education' : 'Saved'}
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-5">
                        <div className="grid gap-4 md:grid-cols-3">
                          <Input data-testid="education-10th-school-input" value={profileData.education?.tenth?.school || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, tenth: { ...profileData.education?.tenth, school: e.target.value } } })} placeholder="10th school" />
                          <Input data-testid="education-10th-board-input" value={profileData.education?.tenth?.board || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, tenth: { ...profileData.education?.tenth, board: e.target.value } } })} placeholder="10th board" />
                          <Input data-testid="education-10th-year-input" value={profileData.education?.tenth?.year || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, tenth: { ...profileData.education?.tenth, year: e.target.value } } })} placeholder="10th year" />
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                          <Input data-testid="education-12th-school-input" value={profileData.education?.twelfth?.school || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, twelfth: { ...profileData.education?.twelfth, school: e.target.value } } })} placeholder="12th school" />
                          <Input data-testid="education-12th-board-input" value={profileData.education?.twelfth?.board || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, twelfth: { ...profileData.education?.twelfth, board: e.target.value } } })} placeholder="12th board" />
                          <Input data-testid="education-12th-year-input" value={profileData.education?.twelfth?.year || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, twelfth: { ...profileData.education?.twelfth, year: e.target.value } } })} placeholder="12th year" />
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                          <Input data-testid="education-graduation-degree-input" value={profileData.education?.graduation?.degree || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, graduation: { ...profileData.education?.graduation, degree: e.target.value } } })} placeholder="Graduation degree" />
                          <Input data-testid="education-graduation-college-input" value={profileData.education?.graduation?.college || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, graduation: { ...profileData.education?.graduation, college: e.target.value } } })} placeholder="Graduation college" />
                          <Input data-testid="education-graduation-year-input" value={profileData.education?.graduation?.year || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, graduation: { ...profileData.education?.graduation, year: e.target.value } } })} placeholder="Graduation year" />
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                          <Input data-testid="education-postgrad-degree-input" value={profileData.education?.postgraduation?.degree || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, postgraduation: { ...profileData.education?.postgraduation, degree: e.target.value } } })} placeholder="Postgrad degree" />
                          <Input data-testid="education-postgrad-college-input" value={profileData.education?.postgraduation?.college || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, postgraduation: { ...profileData.education?.postgraduation, college: e.target.value } } })} placeholder="Postgrad college" />
                          <Input data-testid="education-postgrad-year-input" value={profileData.education?.postgraduation?.year || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, postgraduation: { ...profileData.education?.postgraduation, year: e.target.value } } })} placeholder="Postgrad year" />
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                          <Input data-testid="education-phd-specialization-input" value={profileData.education?.phd?.specialization || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, phd: { ...profileData.education?.phd, specialization: e.target.value } } })} placeholder="PhD specialization" />
                          <Input data-testid="education-phd-university-input" value={profileData.education?.phd?.university || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, phd: { ...profileData.education?.phd, university: e.target.value } } })} placeholder="PhD university" />
                          <Input data-testid="education-phd-year-input" value={profileData.education?.phd?.year || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, phd: { ...profileData.education?.phd, year: e.target.value } } })} placeholder="PhD year" />
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                          <Input data-testid="education-diploma-course-input" value={profileData.education?.diploma?.course || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, diploma: { ...profileData.education?.diploma, course: e.target.value } } })} placeholder="Diploma course" />
                          <Input data-testid="education-diploma-institute-input" value={profileData.education?.diploma?.institute || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, diploma: { ...profileData.education?.diploma, institute: e.target.value } } })} placeholder="Diploma institute" />
                          <Input data-testid="education-diploma-year-input" value={profileData.education?.diploma?.year || ''} onChange={(e) => setProfileData({ ...profileData, education: { ...profileData.education, diploma: { ...profileData.education?.diploma, year: e.target.value } } })} placeholder="Diploma year" />
                        </div>
                        <div>
                          <Label>Other Courses / Certifications</Label>
                          <div className="mt-2 flex gap-2">
                            <Input data-testid="add-course-input" value={newCourse} onChange={(e) => setNewCourse(e.target.value)} placeholder="Add certification or course" />
                            <Button data-testid="add-course-btn" type="button" variant="outline" className="rounded-2xl" onClick={addCourse}>
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {(profileData.education?.other_courses || []).map((course, idx) => (
                              <Badge key={`${course}-${idx}`} variant="secondary" className="rounded-full bg-white px-3 py-1 text-slate-700">
                                {course}
                                <button data-testid={`remove-course-${idx}-btn`} className="ml-2" onClick={() => removeCourse(idx)}>
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border border-slate-200 bg-slate-50/80 shadow-none">
                      <CardHeader>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <CardTitle className="text-lg">Experience</CardTitle>
                            <p className={`mt-1 text-xs font-medium ${experienceDirty ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {experienceDirty ? 'Unsaved changes' : 'Saved'}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant={experienceDirty ? 'default' : 'outline'}
                            className="rounded-full"
                            onClick={() => {
                              if (experienceDraftDirty && !(newExperience.role && newExperience.company_institute && newExperience.duration)) {
                                toast.error('Finish the draft experience and click Add Experience first');
                                return;
                              }
                              handleSaveExperience();
                            }}
                            disabled={savingExperience || (!experienceDirty && !experienceDraftDirty)}
                          >
                            {savingExperience ? 'Saving...' : experienceDirty ? 'Save Experience' : 'Saved'}
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {(profileData.experience || []).map((exp, idx) => (
                          <div key={`${exp.role}-${idx}`} className="flex items-start justify-between rounded-2xl border border-slate-200 bg-white p-4">
                            <div>
                              <div className="font-semibold text-slate-900">{exp.role}</div>
                              <div className="text-sm text-slate-500">{exp.company_institute}</div>
                              <div className="text-sm text-slate-500">{exp.duration}</div>
                            </div>
                            <Button data-testid={`remove-exp-${idx}-btn`} variant="ghost" size="sm" className="rounded-full" onClick={() => removeExperience(idx)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <div className="grid gap-3">
                          <Input data-testid="exp-role-input" value={newExperience.role} onChange={(e) => setNewExperience({ ...newExperience, role: e.target.value })} placeholder="Role" />
                          <Input data-testid="exp-company-input" value={newExperience.company_institute} onChange={(e) => setNewExperience({ ...newExperience, company_institute: e.target.value })} placeholder="Company or institute" />
                          <Input data-testid="exp-duration-input" value={newExperience.duration} onChange={(e) => setNewExperience({ ...newExperience, duration: e.target.value })} placeholder="Duration" />
                          <Button data-testid="add-exp-btn" type="button" variant="outline" className="rounded-2xl" onClick={addExperience}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add Experience
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border border-slate-200 bg-slate-50/80 shadow-none">
                      <CardHeader>
                        <CardTitle className="text-lg">Languages and Teaching Extras</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-5">
                        <div>
                          <Label>Languages You Speak</Label>
                          <div className="mt-2 flex gap-2">
                            <Input
                              data-testid="add-language-input"
                              list="common-languages"
                              value={languageSearch}
                              onChange={(e) => {
                                setLanguageSearch(e.target.value);
                                setNewLanguage(e.target.value);
                              }}
                              placeholder="English, Hindi, Punjabi..."
                            />
                            <Button data-testid="add-language-btn" type="button" variant="outline" className="rounded-2xl" onClick={addLanguage}>
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          <datalist id="common-languages">
                            {commonLanguages.map((language) => <option key={language} value={language} />)}
                          </datalist>
                          {!!languageSearch && filteredLanguages.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {filteredLanguages.map((language) => (
                                <button
                                  key={language}
                                  type="button"
                                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700"
                                  onClick={() => {
                                    setLanguageSearch(language);
                                    setNewLanguage(language);
                                  }}
                                >
                                  {language}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="mt-3 flex flex-wrap gap-2">
                            {(profileData.languages || []).map((lang, idx) => (
                              <Badge key={`${lang}-${idx}`} variant="secondary" className="rounded-full bg-white px-3 py-1 text-slate-700">
                                {lang}
                                <button data-testid={`remove-lang-${idx}-btn`} className="ml-2" onClick={() => removeLanguage(idx)}>
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {profileData.teaches_online && (
                          <div>
                            <Label>Online Teaching Experience</Label>
                            <Input
                              data-testid="online-exp-input"
                              value={profileData.online_experience || ''}
                              onChange={(e) => setProfileData({ ...profileData, online_experience: e.target.value })}
                              className="mt-2"
                              placeholder="e.g. 2 years teaching online"
                            />
                          </div>
                        )}

                        <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                          <div>
                            <div className="font-medium text-slate-900">Homework help</div>
                            <div className="text-sm text-slate-500">Offer assignment and homework support.</div>
                          </div>
                          <Switch
                            data-testid="homework-help-switch"
                            checked={profileData.homework_help || false}
                            onCheckedChange={(checked) => setProfileData({ ...profileData, homework_help: checked })}
                          />
                        </div>

                        <div>
                          <Label>YouTube Intro Video URL</Label>
                          <Input
                            data-testid="intro-video-input"
                            value={profileData.intro_video_url || ''}
                            onChange={(e) => setProfileData({ ...profileData, intro_video_url: e.target.value })}
                            className="mt-2"
                            placeholder="https://youtube.com/watch?v=..."
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 pt-2">
                    <Button
                      variant="outline"
                      className="rounded-full border-slate-300 bg-white"
                      onClick={() => handleSaveProfile(true)}
                      disabled={loading || photoUploading}
                    >
                      Save and See Jobs
                    </Button>
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8"
                      onClick={() => handleSaveProfile(false)}
                      disabled={loading || photoUploading}
                    >
                      {loading ? 'Saving...' : 'Save Complete Profile'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <Button variant="outline" className="rounded-full border-slate-300 bg-white" onClick={() => navigate('/tutor/dashboard')}>
              Cancel
            </Button>
          </div>
          </section>

          <aside className="self-start space-y-6 xl:pt-4">
            <Card className="border-0 bg-white shadow-xl shadow-slate-200/70">
              <CardContent className="p-7">
                <div className="mb-5 flex items-center gap-4">
                  <Avatar className="h-20 w-20 rounded-3xl border-4 border-white shadow-lg">
                    <AvatarImage src={profileData.profile_photo} alt={profileData.name || 'Tutor photo'} />
                    <AvatarFallback className="bg-indigo-100 text-2xl font-semibold text-indigo-700">
                      {profileData.name?.charAt(0) || 'T'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="font-outfit text-2xl font-bold text-slate-900">{profileData.name || 'Your tutor profile'}</h2>
                    <p className="text-sm text-slate-500">
                      {profileData.location || 'Choose your area'} {profileData.subjects?.[0]?.subject ? `• ${profileData.subjects[0].subject}` : ''}
                    </p>
                  </div>
                </div>

                <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  Tutors need a profile photo to appear in public tutor listings. JPG, PNG, and WebP up to 100KB.
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" onClick={() => photoInputRef.current?.click()} disabled={photoUploading}>
                    <Camera className="mr-2 h-4 w-4" />
                    {photoUploading ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/jpg"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
              </CardContent>
            </Card>

            <Card className="border-0 bg-white shadow-xl shadow-slate-200/70">
              <CardContent className="p-7">
                <h3 className="font-semibold text-slate-900">How will students book you?</h3>
                <p className="mt-1 text-sm text-slate-500">Set a simple fee range and at least one teaching mode.</p>

                <div className="mt-5 grid gap-4">
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                    <div>
                      <Label>Minimum Fee (₹) *</Label>
                      <Input
                        data-testid="fee-min-input"
                        type="number"
                        value={profileData.fee_min || ''}
                        onChange={(e) => setProfileData({ ...profileData, fee_min: parseInt(e.target.value, 10) || 0 })}
                        className="mt-2 h-12 rounded-2xl border-slate-200 bg-slate-50"
                        placeholder="300"
                      />
                    </div>
                    <div>
                      <Label>Maximum Fee (₹) *</Label>
                      <Input
                        data-testid="fee-max-input"
                        type="number"
                        value={profileData.fee_max || ''}
                        onChange={(e) => setProfileData({ ...profileData, fee_max: parseInt(e.target.value, 10) || 0 })}
                        className="mt-2 h-12 rounded-2xl border-slate-200 bg-slate-50"
                        placeholder="1000"
                      />
                    </div>
                  </div>

                  <div className="space-y-3 rounded-3xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-medium text-slate-900">Teaching modes *</div>
                    <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-900">Online classes</div>
                        <div className="text-sm text-slate-500">Teach on Zoom, Google Meet, or similar.</div>
                      </div>
                      <Switch
                        data-testid="teaches-online-switch"
                        checked={profileData.teaches_online || false}
                        onCheckedChange={(checked) => setProfileData({ ...profileData, teaches_online: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-900">Tutor home</div>
                        <div className="text-sm text-slate-500">Students can come to your place.</div>
                      </div>
                      <Switch
                        data-testid="teaches-at-home-switch"
                        checked={profileData.teaches_at_home || false}
                        onCheckedChange={(checked) => setProfileData({ ...profileData, teaches_at_home: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-900">Student home</div>
                        <div className="text-sm text-slate-500">You travel to the student’s location.</div>
                      </div>
                      <Switch
                        data-testid="can-travel-switch"
                        checked={profileData.can_travel || false}
                        onCheckedChange={(checked) => setProfileData({ ...profileData, can_travel: checked })}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </main>
    </div>
  );
}
